import { faPalette } from "@fortawesome/pro-regular-svg-icons";
import { faFolder, faShare } from "@fortawesome/pro-solid-svg-icons";
import { Box, SxProps } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import chunk from "lodash/chunk";
import { ReactNode, useCallback, useState } from "react";

import { fileIcon } from "@/components/files/icons";
import { HorizontalAlignment } from "@/types";

import LoadingSpinner from "../../common/LoadingSpinner";
import { FileStatus } from "../../components/graphql/schema.graphql";
import { Icon, Tooltip } from "../../components/ui-v2";
import { AssetIcon } from "../../components/ui-v2/icons";
import { humanFileSize } from "../../utils/humanize";
import { imageExtensionsRegex } from "../files/utils";
import {
  DriveAssetFragment,
  DriveDirectoryFragment,
  DriveFileFragment,
  DriveReferenceSetFragment,
  DriveStyleFragment,
} from "./queries.graphql";
import { DriveItem, DriveSetSearchParams } from "./types";

const itemIcon = (item: DriveItem) => {
  switch (item.__typename) {
    case "Directory":
      return (
        <Icon icon={faFolder} style={{ color: "rgba(56,189,248,0.75)" }} />
      );
    case "Style":
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgb(6,182,212)",
            position: "relative",
            paddingBottom: "10%",
            borderRadius: "4px",
          }}
        >
          <Box sx={{ color: "#022432" }}>
            <Icon icon={faPalette} />
          </Box>

          {item.isShared && (
            <Box
              sx={{
                position: "absolute",
                bottom: "1px",
                right: "1px",
              }}
            >
              <Tooltip title="Shared with you" placement="top" enterDelay={250}>
                <Box
                  sx={{
                    height: "16px",
                    width: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#191A23",
                    fontSize: "12px",
                  }}
                >
                  <Icon icon={faShare} />
                </Box>
              </Tooltip>
            </Box>
          )}
        </Box>
      );
    case "ReferenceSet":
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgb(6,182,212)",
            position: "relative",
            paddingBottom: "10%",
            borderRadius: "4px",
          }}
        >
          <Box sx={{ color: "#022432" }}>
            <Icon icon={faPalette} />
          </Box>
        </Box>
      );
    case "File":
      return (
        <Box
          sx={{
            width: "20px",
            height: "20px",
            borderRadius: "2px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgb(220,38,38)",
            color: "rgb(250,250,255)",
            fontSize: "12px",
          }}
        >
          {fileIcon(item)}
        </Box>
      );
    case "Asset":
      return (
        <Box
          sx={{
            width: "20px",
            height: "20px",
            borderRadius: "2px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgb(59,130,246)",
            color: "rgb(239,246,255)",
            fontSize: "12px",
          }}
        >
          <AssetIcon />
        </Box>
      );
  }
};

export const itemImage = (item: DriveItem) => {
  switch (item.__typename) {
    case "Directory":
    case "Style":
    case "ReferenceSet":
      return itemIcon(item);
    case "File":
      if (
        ["UPLOADED" as FileStatus, "PROCESSED" as FileStatus].includes(
          item.fileStatus,
        )
      ) {
        const previewUrl =
          item.thumbnails?.medium?.url || item.previewUrl || "";
        if (previewUrl.match(imageExtensionsRegex)) {
          return (
            <img
              style={{
                position: "relative",
                objectFit: "contain",
                height: "100%",
                width: "100%",
                display: "block",
                borderRadius: "4px",
              }}
              src={previewUrl}
              alt={item.name ?? undefined}
              loading="lazy"
            />
          );
        } else {
          return (
            <Box
              sx={{
                color: "rgb(220,38,38)",
              }}
            >
              {fileIcon(item)}
            </Box>
          );
        }
      }
      return (
        <Box
          sx={{
            width: "40px",
            height: "40px",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "32px",
          }}
        >
          <LoadingSpinner />
        </Box>
      );
    case "Asset":
      if (item.previewUrl) {
        return (
          <Box
            className="asset-background"
            sx={{
              width: "100%",
              height: "100%",
              backgroundImage:
                "radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 0)",
              backgroundSize: "20px 20px",
              backgroundPosition: "-8px -8px",
            }}
          >
            <img
              style={{
                position: "relative",
                objectFit: "contain",
                height: "100%",
                width: "100%",
                display: "block",
                borderRadius: "4px",
              }}
              src={item.previewUrl}
              alt={item.name}
              loading="lazy"
            />
          </Box>
        );
      } else {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundImage:
                "radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 0)",
              backgroundSize: "20px 20px",
              backgroundPosition: "-8px -8px",
            }}
          />
        );
      }
  }
};

type DriveStyleLikeFragment = DriveStyleFragment | DriveReferenceSetFragment;

type DriveTableViewColumn = {
  id: string;
  label: string;
  className?: string;
  width?: number;
  sx?: SxProps;
  align?: HorizontalAlignment | "justify";
  fileValue?: (file: DriveFileFragment) => ReactNode;
  assetValue?: (asset: DriveAssetFragment) => ReactNode;
  directoryValue?: (directory: DriveDirectoryFragment) => ReactNode;
  styleValue?: (item: DriveStyleLikeFragment) => ReactNode;
};

export const tableViewColumns: DriveTableViewColumn[] = [
  {
    id: "image",
    label: "NAME",
    width: 48,
    sx: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
    fileValue: (file: DriveFileFragment) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "48px",
          width: "48px",
          flexShrink: 0,
          marginLeft: "-16px",
          padding: "4px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "4px",
            fontSize: "21px",
          }}
        >
          {itemImage(file)}
        </Box>
      </Box>
    ),
    assetValue: (asset: DriveAssetFragment) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "48px",
          width: "48px",
          flexShrink: 0,
          marginLeft: "-16px",
          padding: "4px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255, 255, 255, 0.05)",
            borderRadius: "4px",
            fontSize: "21px",
          }}
        >
          {itemImage(asset)}
        </Box>
      </Box>
    ),
    directoryValue: (directory: DriveDirectoryFragment) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "48px",
          width: "48px",
          flexShrink: 0,
          marginLeft: "-16px",
          padding: "4px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255, 255, 255, 0.05)",
            borderRadius: "4px",
            fontSize: "21px",
          }}
        >
          {itemImage(directory)}
        </Box>
      </Box>
    ),
    styleValue: (style: DriveStyleLikeFragment) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "48px",
          width: "48px",
          flexShrink: 0,
          marginLeft: "-16px",
          padding: "4px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255, 255, 255, 0.05)",
            borderRadius: "4px",
            fontSize: "23px",
          }}
        >
          {itemImage(style)}
        </Box>
      </Box>
    ),
  },
  {
    id: "name",
    label: "",
    fileValue: (file: DriveFileFragment) => file.name,
    assetValue: (asset: DriveAssetFragment) => asset.name || "Untitled",
    directoryValue: (directory: DriveDirectoryFragment) => directory.name,
    styleValue: (style: DriveStyleLikeFragment) => style.name || "Untitled",
  },
  {
    id: "createdAt",
    label: "CREATED AT",
    width: 200,
    sx: {
      transform: "translateX(-3px)",
      paddingRight: "8px",
    },
    fileValue: (file: DriveFileFragment) =>
      formatDistanceToNow(file.createdAt, { addSuffix: true }),
    assetValue: (asset: DriveAssetFragment) =>
      formatDistanceToNow(asset.createdAt, { addSuffix: true }),
    directoryValue: (directory: DriveDirectoryFragment) =>
      formatDistanceToNow(directory.createdAt, { addSuffix: true }),
    styleValue: (style: DriveStyleLikeFragment) =>
      formatDistanceToNow(style.createdAt, { addSuffix: true }),
  },
  {
    id: "sizeBytes",
    width: 200,
    sx: {
      transform: "translateX(-2px)",
      paddingRight: "8px",
    },
    label: "SIZE",
    fileValue: (file: DriveFileFragment) => humanFileSize(file.sizeBytes),
  },
];

// Back search params in page state, for dialog views
export const useStateBasedDriveSearchParams = (): [
  URLSearchParams,
  DriveSetSearchParams,
] => {
  const [params, setParams] = useState(() => new URLSearchParams());

  const setSearchParams = useCallback<DriveSetSearchParams>((setter) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      return setter(next);
    });
  }, []);

  return [params, setSearchParams];
};

// Creates a 2d grid of ids based on the number of columns.
export const createGrid = (ids: string[], columns: number): string[][] => {
  return chunk(ids, columns);
};
